import type { FieldValue } from '@bmo/form-core';

import type { RepeatableColumn, WorkflowField } from '@/api/workflows';

import { isMaskedRule } from './fieldValidation';

/** An i18n key under `formRenderer.validation.length` plus the count it names. */
export type LengthViolation = { errorKey: string; params: { count: number } };

/**
 * The cap a single-line ShortText field or column gets when the originator authored none.
 *
 * Free text with NO ceiling reaches the PDF layer, the BMO submit payload and the DDB item as
 * whatever was pasted into it, so "unlimited" was never really unlimited — it just moved the
 * failure downstream. 150 is a deliberate interim figure: generous for a name / address line,
 * tight enough to keep a pasted document out of an answer. An authored `maxLength` always wins,
 * in both directions — a field capped at 10 stays 10, and one capped at 4000 stays 4000.
 */
export const DEFAULT_MAX_LENGTH = 150;

/**
 * The same fallback for a multi-line LongText, which is asked for prose rather than a field value.
 *
 * 150 is about two lines — a cap a legitimate "describe the circumstances" answer would hit — so
 * the textarea gets its own, roughly a short paragraph. Split from {@link DEFAULT_MAX_LENGTH}
 * deliberately: the two controls are capped for the same REASON but at different scales, and
 * folding them back together would re-pinch the only input meant to hold sentences.
 */
export const DEFAULT_LONG_TEXT_MAX_LENGTH = 500;

/** The uncapped fallback for a text input of the given type. */
function defaultMaxLength(type: 'ShortText' | 'LongText'): number {
  return type === 'LongText' ? DEFAULT_LONG_TEXT_MAX_LENGTH : DEFAULT_MAX_LENGTH;
}

/**
 * The cap actually in force: the authored one, or the type's default when there is none.
 *
 * `0` and negative values read as "unset" here, matching {@link lengthViolation}'s own treatment
 * of them — so a column carrying `maxLength: 0` gets the default rather than an impossible
 * must-be-empty rule.
 *
 * `minLength` is consulted only to keep the default from contradicting an authored floor: the BE
 * checks `min <= max` only when BOTH are set, so a field authored with `minLength: 200` and no
 * maximum is legal, and a blanket ceiling below it would make it impossible to fill. An explicit
 * minimum wins over an implicit default.
 */
export function effectiveMaxLength(
  type: 'ShortText' | 'LongText',
  maxLength: number | null | undefined,
  minLength?: number | null,
): number {
  if (maxLength && maxLength > 0) return maxLength;
  const fallback = defaultMaxLength(type);
  return minLength && minLength > fallback ? minLength : fallback;
}

/**
 * Whether a typed text breaks an authored minimum / maximum length — and which message says so.
 *
 * The DOM `maxlength` attribute only stops KEYSTROKES; a pasted or scripted fill walks straight
 * past it, and nothing checked `minLength` at all (QA: fund number `1234` against a 5-character
 * field, transit `123456` against 5, both accepted). This is the check itself.
 *
 * Blank is never a length failure — `required` owns that — and the text is measured without its
 * outer spaces, the same text the blur trim keeps. A min equal to the max reads "exactly".
 */
export function lengthViolation(
  text: string,
  minLength: number | null | undefined,
  maxLength: number | null | undefined,
): LengthViolation | null {
  const n = text.trim().length;
  if (n === 0) return null;
  const min = minLength && minLength > 0 ? minLength : null;
  const max = maxLength && maxLength > 0 ? maxLength : null;
  const short = min !== null && n < min;
  const long = max !== null && n > max;
  if (!short && !long) return null;
  if (min !== null && min === max) {
    return { errorKey: 'formRenderer.validation.length.exact', params: { count: min } };
  }
  return short
    ? { errorKey: 'formRenderer.validation.length.min', params: { count: min! } }
    : { errorKey: 'formRenderer.validation.length.max', params: { count: max! } };
}

/**
 * {@link lengthViolation} for a whole field — ShortText and LongText only.
 *
 * Skipped where something else owns the length: a display mask (the stored value is bare digits
 * and the mask decides how many) and a sin / phone / postal rule (the stored value is FORMATTED,
 * so its length is not what the advisor counts, and the rule validates it itself).
 */
export function fieldLengthViolation(
  field: Pick<WorkflowField, 'type' | 'minLength' | 'maxLength' | 'displayMask' | 'validate'>,
  value: FieldValue,
): LengthViolation | null {
  if (field.type !== 'ShortText' && field.type !== 'LongText') return null;
  if (typeof value !== 'string') return null;
  if (field.displayMask || isMaskedRule(field.validate)) return null;
  return lengthViolation(
    value,
    field.minLength,
    effectiveMaxLength(field.type, field.maxLength, field.minLength),
  );
}

/**
 * {@link lengthViolation} for one cell of a RepeatableRow column — ShortText and LongText only.
 *
 * A column applies no mask (its `validate` is not wired to one), so the stored text is what the
 * advisor typed and is measured as it stands. Blank passes: an optional cell may stay empty, and
 * a required one is reported as missing, not as short.
 */
export function columnLengthViolation(
  col: Pick<RepeatableColumn, 'type' | 'minLength' | 'maxLength'>,
  value: unknown,
): LengthViolation | null {
  if (col.type !== 'ShortText' && col.type !== 'LongText') return null;
  if (typeof value !== 'string') return null;
  return lengthViolation(
    value,
    col.minLength,
    effectiveMaxLength(col.type, col.maxLength, col.minLength),
  );
}
